import {users} from '@cl/shared';
import {
  TableFilterConditionsConnection,
  TableFilterEqualityOperator,
  TableRangeOperator,
} from './filterTableTool.enums';
import {FilterToolSchemaField, TableFilterConditionGroup} from './filterTableTool.types';
import filterTableDataWithGroups from './utils/filterTableDataWithGroups';
import {ToolExecuteParams} from '../../copilot.types';
import {DateTime} from 'luxon';
import openAIUserMessage from '../../openAI/utils/openAIUserMessage';
import openAISystemMessage from '../../openAI/utils/openAISystemMessage';
import {z} from 'zod';
import invokeStandaloneLLMCall from '../../utils/invokeStandaloneLLMCall';
import toolResult from '../../utils/creators/toolResult';
import {ToolName} from '../../copilot.enums';
import tool from '../../utils/creators/tool';

const inputSchema = z.object({
  prompt: z
    .string()
    .describe(
      'The user prompt describing the filter, e.g., "Filter rows named John and aged over 40"',
    ),
});

/***
 * This tool is used to translate a user prompt into structured filter conditions.
 * Applying the conditions and filtering the data is done by the caller.
 */
function filterTableTool<DataType extends object>({
  dataTypeSchema,
  displayName,
  fetchData,
  filterFunction,
  clearFilter,
}: {
  /**
   * The schema of the data to filter.
   */
  dataTypeSchema: FilterToolSchemaField<DataType>[];
  /**
   * The name of the tool.
   */
  displayName?: string;

  /**
   * The function to fetch the data to filter.
   */
  fetchData: () => DataType[];

  /**
   * The function to update the data after filtering.
   */
  filterFunction: (data: DataType[], query: string) => void;
  clearFilter: () => void;
}) {
  async function execute({input: {prompt}, abortSignal}: ToolExecuteParams<typeof inputSchema>) {
    const {userSubject} = users;
    const user = userSubject.data;

    const systemPrompt = `You are an expert at translating user filter requests into structured filter conditions for tables.
     Data schema: ${JSON.stringify(dataTypeSchema)}.
    
    Structure your response as condition groups where:
    - Each group contains related conditions connected by AND/OR
    - Groups themselves are connected by AND/OR
    
    For example: "type equals 10 OR type equals 5 AND name contains 'a' OR name contains 'b'"
    Should be structured as:
    - Group 1: (type equals 10 OR type equals 5) 
    - Group 2: (name contains 'a' OR name contains 'b')
    - Groups connected by AND
    
    When asked to remove or clear filters, return empty conditionGroups array.
    Current date is ${DateTime.now().toISO()}.
    The user's name is '${user.firstName} ${user.lastName}'`;

    const userMessage = openAIUserMessage(prompt);
    const messages = [openAISystemMessage(systemPrompt), userMessage];

    const conditionSchema = z
      .object({
        property: z.string(),
        operator: z.nativeEnum(TableFilterEqualityOperator),
        value: z.string(),
      })
      .or(
        z.object({
          property: z.string(),
          operator: z.literal(TableRangeOperator.BetweenRange),
          minValue: z.string(),
          maxValue: z.string(),
        }),
      );

    const response = await invokeStandaloneLLMCall({
      messages,
      responseSchema: z.object({
        conditionGroups: z.array(
          z.object({
            conditions: z.array(conditionSchema),
            conditionsConnection: z.nativeEnum(TableFilterConditionsConnection),
          }),
        ),
        groupsConnection: z.nativeEnum(TableFilterConditionsConnection),
        querySummary: z
          .string()
          .describe('A summary of the query, e.g., "Name is John and age is greater than 30"'),
      }),
      abortSignal,
    });

    const conditionGroups = response.conditionGroups ?? [];

    if (conditionGroups.length === 0) {
      clearFilter();

      return toolResult('Table filters cleared.');
    }

    const rawData = fetchData();
    const filteredData = filterTableDataWithGroups(rawData, {
      conditionGroups: conditionGroups as TableFilterConditionGroup[],
      groupsConnection: response.groupsConnection,
    });

    filterFunction(filteredData, response.querySummary);

    return toolResult(`Rows count after filtering: ${filteredData.length}`);
  }

  return tool({
    name: ToolName.FilterTable,
    displayName: displayName ?? 'Filtering Table',
    description: 'Filter table data',
    execute,
    inputSchema,
  });
}

export default filterTableTool;
